// Periods (one UTC day each): key, kit, three instruments, FX, break and seed (SPEC §2). The
// server stores `PeriodDef`s; clients and the check worker turn them into the engine's
// `PeriodSpec` with the kit's manifest.

import { z } from "zod";
import { generateBreak, generateDawn } from "./breaks";
import type { FxSettings } from "./generated/FxSettings";
import type { KitManifest } from "./generated/KitManifest";
import type { LoopData } from "./generated/LoopData";
import type { NoteEvent } from "./generated/NoteEvent";
import type { PeriodSound } from "./generated/PeriodSound";
import type { PeriodSpec } from "./generated/PeriodSpec";
import type { SoundMix } from "./generated/SoundMix";
import { defaultAtmosKit } from "./atmos";
import { ATMOS_KIT_IDS, getAtmosKit, type AtmosKitId, type AtmosPad } from "./atmosKits";
import { classicAlternates, defaultClassic } from "./classics";
import type { Bpm } from "./clock";
import { KIT_IDS, KITS, kitSampleId, type KitId } from "./kits";
import { keyName, type Key } from "./music";
import { getPreset, INSTRUMENT_ROLES, pickAlternates, ROLE_RANGE, type InstrumentRole } from "./presets";

export const NUM_PADS = 16;
export const BASS_SLOT = 16;
export const HARMONY_SLOT = 17;
export const LEAD_SLOT = 18;
export const CLASSIC_SLOT = 19;
/** Instrument slots (bass, harmony, lead, classic). */
export const NUM_INSTRUMENTS = 4;
/** The day's atmos kit: 12 pads (20–31) after the instruments — atmosphere and gentle effects,
 *  played like drum pads (each pad tuned to the day's key; an event's note is ignored). */
export const FIRST_ATMOS = 20;
export const NUM_ATMOS_PADS = 12;
export const NUM_SOUNDS = FIRST_ATMOS + NUM_ATMOS_PADS;
/** Alternates per instrument (variants 1–2). */
export const ALTS_PER_INSTRUMENT = 2;
/** Sounds a song can play: the 32 slots, then each instrument's two alternates as extra lanes
 *  (sound ids 32–39: bass a/b, harmony a/b, lead a/b, classic a/b). */
export const NUM_LANE_SOUNDS = NUM_SOUNDS + NUM_INSTRUMENTS * ALTS_PER_INSTRUMENT;
/** Mix lengths of older songs with extra lanes (`upgradeLoop` moves them to today's layout):
 *  before any atmos (20 slots + 8 lanes on 20–27) and with the single atmos slot (21 + 10 on 21–30). */
export const OLD_LANE_MIX = 28;
export const ATMOS1_LANE_MIX = 31;
/** Is `pad` one of the atmos kit's pads (20–31)? */
export const isAtmosPad = (pad: number) => pad >= FIRST_ATMOS && pad < NUM_SOUNDS;
/** The extra-lane sound id of instrument `slot`'s alternate `variant` (1–2). */
export const altSound = (slot: number, variant: number) =>
  NUM_SOUNDS + (slot - NUM_PADS) * ALTS_PER_INSTRUMENT + variant - 1;
/** The instrument slot a sound belongs to (itself for 0–31). */
export const laneSlot = (pad: number) =>
  pad < NUM_SOUNDS ? pad : NUM_PADS + Math.floor((pad - NUM_SOUNDS) / ALTS_PER_INSTRUMENT);
/** The variant an extra lane plays (1–2); 0 for the slots themselves. */
export const laneVariant = (pad: number) => (pad < NUM_SOUNDS ? 0 : ((pad - NUM_SOUNDS) % ALTS_PER_INSTRUMENT) + 1);
export const ROLE_SLOT: Record<InstrumentRole, number> = {
  bass: BASS_SLOT,
  harmony: HARMONY_SLOT,
  lead: LEAD_SLOT,
  classic: CLASSIC_SLOT,
};
/** Instrument role of an instrument slot (16–19) or extra lane (32–39). */
export const SLOT_ROLE: Record<number, InstrumentRole> = Object.fromEntries([
  ...INSTRUMENT_ROLES.map((r, i) => [NUM_PADS + i, r] as const),
  ...Array.from({ length: NUM_LANE_SOUNDS - NUM_SOUNDS }, (_, i) => {
    const pad = NUM_SOUNDS + i;
    return [pad, INSTRUMENT_ROLES[laneSlot(pad) - NUM_PADS]] as const;
  }),
]);
/** Display names of the instrument categories. */
export const ROLE_LABEL: Record<InstrumentRole, string> = {
  bass: "Bass",
  harmony: "Harmony",
  lead: "Lead",
  classic: "Classics",
};

/** Tempo-synced delay times a period may use (ticks: 1/8, dotted 1/8, 1/4, dotted 1/4). */
export const DELAY_TICKS = [480, 720, 960, 1440] as const;

export const noteEventSchema = z.object({
  tick: z.number().int(),
  pad: z.number().int(),
  note: z.number().int().min(0).max(127),
  velocity: z.number().int(),
  length_ticks: z.number().int(),
});

export const keySchema = z.object({ tonic: z.number().int().min(0).max(11), mode: z.enum(["major", "minor"]) });

export const periodDefSchema = z.object({
  /** The stream: 72 for the slow sister stream; missing means the main stream (125 BPM). */
  bpm: z.literal(72).optional(),
  day: z.number().int(),
  key: keySchema,
  kit: z.enum(KIT_IDS as [KitId, ...KitId[]]),
  /** `classic` is missing in days planned before the Classics category (`normalizePeriodDef`). */
  instruments: z.object({ bass: z.string(), harmony: z.string(), lead: z.string(), classic: z.string() }),
  /** Two alternates per instrument: from the main preset's family, the classic's from two other
   *  classic families (derived from the seed when absent). */
  alternates: z
    .object({
      bass: z.tuple([z.string(), z.string()]),
      harmony: z.tuple([z.string(), z.string()]),
      lead: z.tuple([z.string(), z.string()]),
      classic: z.tuple([z.string(), z.string()]),
    })
    .optional(),
  /** The day's atmos kit (atmosKits.ts); missing in days planned before atmos kits. */
  atmosKit: z.enum(ATMOS_KIT_IDS),
  fx: z.object({ reverbSize: z.number().min(0).max(1), delayTicks: z.number().int().min(120).max(3840) }),
  seed: z.number().int().min(0).max(0xffffffff),
  breakEvents: z.array(noteEventSchema).max(64),
  dawnEvents: z.array(noteEventSchema).max(64),
  /** Short label of the day's character ("Jungle · round bass · lush pads"). */
  genre: z.string().max(80),
});

export type PeriodDef = z.infer<typeof periodDefSchema>;

/**
 * Days planned before the Classics category have no classic instrument, and days planned before
 * atmos kits no atmos kit (stored definitions): give them those, chosen from the day's seed and
 * kit (and the classic's alternates). A day's three classic instruments (main + two alternates)
 * come from three different families: stored days from before that rule (three violins) get new
 * alternates. Idempotent.
 */
export function normalizePeriodDef<
  T extends Pick<PeriodDef, "kit" | "seed" | "instruments" | "alternates"> & { atmosKit?: AtmosKitId },
>(def: T): T & { atmosKit: AtmosKitId } {
  const inst = def.instruments as Partial<PeriodDef["instruments"]>;
  const alts = def.alternates as Partial<NonNullable<PeriodDef["alternates"]>> | undefined;
  const atmosKit = def.atmosKit ?? defaultAtmosKit(def.kit, def.seed);
  const others = [inst.bass ?? "", inst.harmony ?? "", inst.lead ?? ""].filter(Boolean);
  if (inst.classic && alts?.classic && !threeFamilies(inst.classic, alts.classic)) {
    const classic = classicAlternates(inst.classic, def.seed >>> 0, def.kit, others);
    return { ...def, alternates: { ...alts, classic } as PeriodDef["alternates"], atmosKit };
  }
  if (inst.classic && (!alts || alts.classic))
    return def.atmosKit ? (def as T & { atmosKit: AtmosKitId }) : { ...def, atmosKit };
  const classic = inst.classic ?? defaultClassic(def.kit, def.seed, [], others);
  const instruments = { ...def.instruments, classic };
  const alternates = alts
    ? { ...alts, classic: alts.classic ?? classicAlternates(classic, def.seed >>> 0, def.kit, others) }
    : def.alternates;
  return { ...def, instruments, alternates, atmosKit };
}

/** True when a day's classic instruments are all from different families. */
function threeFamilies(main: string, alts: readonly string[]): boolean {
  try {
    return new Set([main, ...alts].map((id) => getPreset(id).family)).size === 1 + alts.length;
  } catch {
    return true; // an unknown id: leave the day as it is
  }
}

/** Parse a stored period definition (accepts days from before the Classics category and atmos kits). */
export function parsePeriodDef(raw: unknown): PeriodDef {
  const r = raw as PeriodDef;
  return periodDefSchema.parse(r && typeof r === "object" && r.instruments ? normalizePeriodDef(r) : r);
}

export function periodFx(def: Pick<PeriodDef, "fx">): FxSettings {
  return {
    reverb_size: def.fx.reverbSize,
    reverb_damping: 0.4,
    reverb_return: 0.8,
    delay_ticks: def.fx.delayTicks,
    delay_feedback: 0.35,
    delay_tone: 0.6,
    delay_return: 0.7,
  };
}

const DRUM_ENVELOPE = { attack_ms: 0.5, decay_ms: 200, sustain: 1, release_ms: 60 };

/** Build the engine's period spec. `manifest` is the kit's `kit.json`. */
export function periodSpec(def: PeriodDef, manifest: KitManifest): PeriodSpec {
  const sounds: PeriodSound[] = manifest.pads.slice(0, NUM_PADS).map((kp) => ({
    name: kp.name,
    sample_id: kitSampleId(def.kit, kp.file),
    synth: null,
    envelope: kp.mode === "gate" ? { ...DRUM_ENVELOPE, release_ms: 180 } : { ...DRUM_ENVELOPE },
    mode: kp.mode,
    choke_group: kp.choke_group,
    tune: 0,
    default_mix: {
      volume: Math.min(1, kp.volume),
      pan: kp.pan,
      cutoff: 20000,
      reverb_send: Math.min(0.6, kp.reverb_send),
      delay_send: Math.min(0.5, kp.delay_send),
    },
  }));
  for (const role of INSTRUMENT_ROLES) sounds.push(instrumentSound(def.instruments[role]));
  // The atmos kit's twelve pads, tuned to the day's key.
  const kit = getAtmosKit(def.atmosKit ?? defaultAtmosKit(def.kit, def.seed));
  kit.pads.forEach((p, i) => sounds.push(atmosSound(p, i, def.key)));
  const alts = periodAlternates(def);
  return {
    day: def.day,
    bpm: def.bpm ?? 125,
    sounds,
    alternates: INSTRUMENT_ROLES.flatMap((role) => alts[role].map(instrumentSound)),
    fx: periodFx(def),
    break_events: def.breakEvents,
    break_roles: KITS[def.kit].roles,
    dawn_events: def.dawnEvents,
    seed: def.seed >>> 0,
  };
}

/**
 * Default instrument levels: the mix balance songs start with (and Wizard songs use). Presets are
 * calibrated to equal loudness per role (`params.level`); these volumes then place each role
 * against the drums like an engineer would for instrumental dance music — measured on Wizard
 * songs across all kits: bass ≈ −3 LU, lead ≈ −5.5 LU, chords ≈ −7.5 LU below the drums
 * (server/test/mix.test.ts keeps it there).
 */
export const ROLE_VOLUME: Record<InstrumentRole, number> = {
  bass: 0.74,
  harmony: 0.47,
  lead: 0.59,
  classic: 0.62,
};
/** The atmos kit's default volume: a background layer, well under the chords (pads are
 *  calibrated to equal loudness; a pad's own `volume` scales this). */
export const ATMOS_VOLUME = 0.28;
/** Default low-pass per role: bass keeps out of the hats' and lead's range, chords leave the top
 *  to the hats and the lead's harmonics (writers can open them with the filter knob). */
export const ROLE_CUTOFF: Record<InstrumentRole, number> = {
  bass: 6000,
  harmony: 9000,
  lead: 20000,
  classic: 20000,
};

const MODE_STEPS = { major: [0, 2, 4, 5, 7, 9, 11], minor: [0, 2, 3, 5, 7, 8, 10] } as const;

/** The MIDI note of an atmos pad in `key`: its scale degree's pitch class, within the octave of its
 *  C (so a pad keeps its register whatever the key). */
export function atmosPadNote(p: Pick<AtmosPad, "degree" | "octave">, key: Key): number {
  const d = ((p.degree % 7) + 7) % 7;
  return p.octave + ((key.tonic + MODE_STEPS[key.mode][d]) % 12);
}

/** Stereo places for the twelve pads (spread gently around the centre). */
const ATMOS_PAN = [-0.2, 0.2, -0.35, 0.35, -0.1, 0.1, -0.45, 0.45, -0.25, 0.25, 0, 0];

function atmosSound(p: AtmosPad, i: number, key: Key): PeriodSound {
  return {
    name: p.name,
    sample_id: null,
    synth: { ...p.params, note: atmosPadNote(p, key) },
    envelope: { ...p.env },
    mode: "gate",
    choke_group: 0,
    tune: 0,
    default_mix: {
      volume: Math.round(ATMOS_VOLUME * (p.volume ?? 1) * 1000) / 1000,
      pan: ATMOS_PAN[i] ?? 0,
      cutoff: 20000,
      reverb_send: Math.min(0.6, p.reverb),
      delay_send: Math.min(0.5, p.delay),
    },
  };
}

function instrumentSound(id: string): PeriodSound {
  const preset = getPreset(id);
  return {
    name: preset.name,
    sample_id: null,
    synth: { ...preset.params },
    envelope: { ...preset.env },
    mode: preset.mode,
    choke_group: 0,
    tune: 0,
    default_mix: {
      volume: ROLE_VOLUME[preset.role],
      pan: 0,
      cutoff: ROLE_CUTOFF[preset.role],
      reverb_send: Math.min(0.6, preset.reverb),
      delay_send: Math.min(0.5, preset.delay),
    },
  };
}

/** The day's alternates per instrument (stored, or derived from main preset and seed). */
export function periodAlternates(
  def: Pick<PeriodDef, "instruments" | "seed" | "alternates"> & { kit?: KitId },
): Record<InstrumentRole, [string, string]> {
  const derive = (role: InstrumentRole, i: number): [string, string] => {
    if (role !== "classic") return pickAlternates(def.instruments[role], ((def.seed >>> 0) + i * 7919) >>> 0);
    // The classic instruments: three in total, each from its own family.
    const kit = def.kit ?? "jungle";
    const { bass, harmony, lead } = def.instruments;
    return classicAlternates(def.instruments.classic ?? defaultClassic(kit, def.seed), def.seed >>> 0, kit, [
      bass,
      harmony,
      lead,
    ]);
  };
  if (def.alternates) {
    // Stored (e.g. chosen by an admin); days from before the Classics category get theirs derived.
    return def.alternates.classic ? def.alternates : { ...def.alternates, classic: derive("classic", 3) };
  }
  const out = {} as Record<InstrumentRole, [string, string]>;
  INSTRUMENT_ROLES.forEach((role, i) => (out[role] = derive(role, i)));
  return out;
}

/** Preset ids instrument `role` can play in this period: main, alternate 1, alternate 2. */
export function instrumentChoices(def: Pick<PeriodDef, "instruments" | "seed" | "alternates">, role: InstrumentRole) {
  return [def.instruments[role], ...periodAlternates(def)[role]] as [string, string, string];
}

/** The default mix of every sound a song can play (21 slots, then the 10 extra-lane alternates). */
export function defaultMix(spec: PeriodSpec): SoundMix[] {
  return Array.from({ length: NUM_LANE_SOUNDS }, (_, i) => ({
    ...(i < NUM_SOUNDS ? spec.sounds[i] : (spec.alternates[i - NUM_SOUNDS] ?? spec.sounds[laneSlot(i)])).default_mix,
  }));
}

export function emptyLoop(spec: PeriodSpec): LoopData {
  return { events: [], mix: defaultMix(spec).slice(0, NUM_SOUNDS) };
}

/** A period definition from its ingredients, with break and dawn generated from the seed. */
export function makePeriodDef(input: {
  /** The stream's tempo (125 = main, the default). */
  bpm?: Bpm;
  day: number;
  key: Key;
  kit: KitId;
  /** The Classics instrument is chosen from the kit and seed when not given. */
  instruments: Omit<Record<InstrumentRole, string>, "classic"> & { classic?: string };
  /** The atmos kit; chosen from the kit and seed when not given. */
  atmosKit?: AtmosKitId;
  fx: { reverbSize: number; delayTicks: number };
  seed: number;
  /** Explicit alternates (e.g. chosen by an admin); derived from the mains and seed otherwise. */
  alternates?: Omit<NonNullable<PeriodDef["alternates"]>, "classic"> & { classic?: [string, string] };
}): PeriodDef {
  const seed = input.seed >>> 0;
  const { bass, harmony, lead } = input.instruments;
  const instruments = {
    ...input.instruments,
    classic: input.instruments.classic ?? defaultClassic(input.kit, seed, [], [bass, harmony, lead]),
  };
  // The classic's alternates come from two other families (a day offers three different classic
  // instruments); the other instruments' from their main's family.
  const derived = {
    ...periodAlternates({ instruments, seed }),
    classic: classicAlternates(instruments.classic, seed, input.kit, [bass, harmony, lead]),
  };
  const slow = input.bpm === 72;
  return {
    ...(slow ? { bpm: 72 as const } : {}),
    day: input.day,
    key: input.key,
    kit: input.kit,
    instruments,
    alternates: input.alternates
      ? { ...input.alternates, classic: input.alternates.classic ?? derived.classic }
      : derived,
    fx: input.fx,
    seed,
    atmosKit: input.atmosKit ?? defaultAtmosKit(input.kit, seed),
    breakEvents: generateBreak(input.kit, seed, input.bpm ?? 125),
    dawnEvents: generateDawn(input.kit, seed),
    genre: genreLabel(input.kit, instruments),
  };
}

export function genreLabel(kit: KitId, instruments: Record<InstrumentRole, string>): string {
  const bass = getPreset(instruments.bass);
  const harmony = getPreset(instruments.harmony);
  return `${KITS[kit].name} · ${bass.flavour} bass · ${harmony.flavour} ${harmony.name.split(" ").pop()!.toLowerCase()}`;
}

/** A one-line description of a period for headers and the calendar. */
export function describePeriod(def: PeriodDef): string {
  return `${keyName(def.key)} · ${KITS[def.kit].name} · ${INSTRUMENT_ROLES.map((r) => getPreset(def.instruments[r]).name).join(", ")}`;
}

/** The sound a loop plays on `slot` (its chosen alternate for instruments). */
export function loopSound(spec: PeriodSpec, variants: LoopData["variants"], slot: number): PeriodSound | undefined {
  if (slot >= NUM_SOUNDS) return spec.alternates[slot - NUM_SOUNDS] ?? spec.sounds[laneSlot(slot)];
  const v = slot >= BASS_SLOT && slot <= CLASSIC_SLOT ? (variants?.[slot - BASS_SLOT] ?? 0) : 0;
  if (Number.isInteger(v) && v >= 1 && v <= 2) {
    const alt = spec.alternates[(slot - BASS_SLOT) * 2 + v - 1];
    if (alt) return alt;
  }
  return spec.sounds[slot];
}

/** Note range per instrument slot. */
export function slotRange(slot: number): { lo: number; hi: number } | null {
  const role = SLOT_ROLE[slot];
  return role ? ROLE_RANGE[role] : null;
}

export type { NoteEvent };
